import { createVuetify } from 'vuetify';
import { aliases, mdi } from 'vuetify/iconsets/mdi';
import { en as vEn, ar as vAr, ckb as vCkb } from 'vuetify/locale';
import enApp from '../locales/en.json';
import arApp from '../locales/ar.json';
import ckbApp from '../locales/ckb.json';

import { ref, shallowRef } from 'vue';

function getObjectValueByPath(obj, path, fallback) {
    if (!obj || !path) return fallback;
    if (obj[path] !== undefined) return obj[path];
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
        if (value && typeof value === 'object') {
            value = value[key];
        } else {
            return fallback;
        }
    }
    return value !== undefined ? value : fallback;
}

const customLocaleAdapter = (options) => {
    const current = shallowRef(options?.locale ?? 'en');
    const fallback = shallowRef(options?.fallback ?? 'en');
    const messages = ref(options?.messages ?? {});

    const t = (key, ...params) => {
        const shortKey = key.startsWith('$vuetify.') ? key.replace('$vuetify.', '') : key;
        const currentLocale = current.value && messages.value[current.value];
        const fallbackLocale = fallback.value && messages.value[fallback.value];

        let str = getObjectValueByPath(currentLocale, shortKey, null);
        if (!str) str = getObjectValueByPath(fallbackLocale, shortKey, null);
        if (!str) str = key;
        if (typeof str !== 'string') str = key;

        return str.replace(/\{(\d+)\}/g, (match, index) => String(params[Number(index)]));
    };

    const n = (value, options) => {
        return new Intl.NumberFormat([current.value, fallback.value], options).format(value);
    };

    const decimalSeparator = shallowRef(
        typeof Intl !== 'undefined' && Intl.NumberFormat
            ? (new Intl.NumberFormat([current.value, fallback.value]).format(0.1).includes(',') ? ',' : '.')
            : '.'
    );

    const provide = (props) => {
        return {
            name: 'vuetify',
            current,
            fallback,
            messages,
            t,
            n,
            decimalSeparator,
            provide
        };
    };

    return {
        name: 'vuetify',
        current,
        fallback,
        messages,
        t,
        n,
        decimalSeparator,
        provide
    };
};

export default createVuetify({
    locale: {
        adapter: customLocaleAdapter({
            locale: 'ckb',
            fallback: 'ckb',
            messages: {
                en: { ...vEn, ...enApp },
                ar: { ...vAr, ...arApp },
                ckb: { ...vCkb, ...ckbApp }
            }
        }),
        rtl: {
            ar: true,
            ckb: true,
        },
    },
    date: {
        locale: {
            en: 'en-US',
            ckb: 'ar-IQ' // Map ckb to Arabic (Iraq) for proper RTL calendar layout and numerals
        }
    },
    theme: {
        defaultTheme: 'light',
        themes: {
            light: {
                dark: false,
                colors: {
                    primary: '#03739C', // Medical Oceanic Cyan (matches Flutter AppTheme)
                    'primary-darken-1': '#025777', // Deep Marine Cyan
                    secondary: '#0D9488', // Medical Teal Accent
                    accent: '#0EA5E9', // Vibrant Sky Cyan
                    error: '#EF4444', // Clean medical danger red
                    info: '#0284C7', // Oceanic Sky Blue
                    success: '#10B981', // Medical Emerald
                    warning: '#F59E0B', // Amber Gold
                    background: '#F8FAFC', // Ceramic Slate
                    surface: '#FFFFFF', // Pure White
                    'surface-light': '#F1F5F9',
                    'surface-variant': '#F1F5F9',
                    'surface-bright': '#FFFFFF',
                    'on-surface': '#0F172A',
                    'on-surface-variant': '#64748B',
                    outline: '#E2E8F0',
                },
            },
        }
    },
    icons: {
        defaultSet: 'mdi',
        aliases,
        sets: {
            mdi,
        },
    },
});

if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('theme');
}
if (typeof document !== 'undefined') {
    document.documentElement.classList.remove('dark');
}

